// FilterDropdown.js
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

/** Utilities */
import formatDate from "../../../../../../FinalMolGrid/libs/formatDate";
import { useTaskFilter } from "../../../../../../../utils/Context/TaskFilterContext";

/** Common Components */
import DropdownSelect from "../../../../../../ui/dropdown/DropdownSelect";
import Iconify from "../../../../../../../layouts/components/iconify/iconify";
import { DropdownWrapper } from "../../../../../../ui/dropdownwrapper/DropdownWrapper";

/** Configuration Files */
import { filterCompare, filterDropdownKeys } from "../../../../../../../data/FilterKey";

/** RTK Query */
import { useLazyGetAllTaskTypeListQuery } from "../../../../../../../app/services/taskTypeAPI";
import { useLazyGetAllPriorityListQuery } from "../../../../../../../app/services/priorityAPI";
import { useGetAllProjectTeamListMutation } from "../../../../../../../app/services/projectTeamManagementAPI";
import { useGetProjectStatusWithPaginationMutation } from "../../../../../../../app/services/projectStatusAPI";
import { useLazyGetAssignProjectListByUserIdQuery } from "../../../../../../../app/services/projectManagementAPI";
import { useGetPlanningListByUserIdAndProjectIdMutation } from "../../../../../../../app/services/teamPlanningAPI";

/** CSS Files */
import "./FilterDropdown.scss";

const FilterDropdownTest = ({ projectId, taskFilterPageId }) => {

    const { filters, filterRequest, applyFilter, addFilter, updateFilter, deleteFilter, clearFilters } = useTaskFilter();

    const userId = useSelector((state) => state.auth.user.userId);

    const [getAllPriorityList, {
        isFetching: isPriorityFetching,
        isSuccess: isPrioritySuccess,
        data: isPriorityData,
    },] = useLazyGetAllPriorityListQuery();

    const [getAllTaskTypeList, {
        isFetching: isTaskTypeFetching,
        isSuccess: isTaskTypeFetched,
        data: TaskTypeData,
    },] = useLazyGetAllTaskTypeListQuery();

    const [getAllTaskStatus, {
        isFetching: isTaskStatusfetching,
        isSuccess: isTaskStatussuccess,
        data: isTaskStatusData,
    },] = useGetProjectStatusWithPaginationMutation();

    const [getUserList, {
        isFetching: isUserNameFetching,
        isSuccess: isUserName,
        data: UserNameData,
    },] = useGetAllProjectTeamListMutation();

    const [getPlanningListByUserIdAndProjectId, {
        isFetching: isgetTeamPlanningListByUserIdfetching,
        isSuccess: isgetTeamPlanningListByUserIdsuccess,
        data: isgetTeamPlanningListByUserIdData,
    },] = useGetPlanningListByUserIdAndProjectIdMutation();

    const [getAssignProjectListByUserId, {
        isSuccess: isListSuccess,
        data: ProjectList
    },] = useLazyGetAssignProjectListByUserIdQuery();

    const [projects, setProjects] = useState([]);
    const [userList, setUserList] = useState([]);
    const [taskTypes, setTaskTypes] = useState([]);
    const [taskStatus, setTaskStatus] = useState([]);
    const [planningList, setPlanningList] = useState([]);
    const [taskPriority, setTaskPriority] = useState([]);

    useEffect(() => {
        getAllTaskTypeList();
        getAllPriorityList();
        getUserListByProjectId();
        getStatusListByProjectId();
        if (userId) {
            getAssignProjectListByUserId(userId);
            getPlanningListByUserIdAndProjectId({ userId, projectId })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, projectId]);

    const getStatusListByProjectId = () => {
        const request = {
            pagination: {
                pageNumber: 1,
                pageSize: 40,
            },
            projectId: projectId,
        };
        getAllTaskStatus(request);
    };

    const getUserListByProjectId = () => {
        const request = {
            pagination: {
                pageNumber: 1,
                pageSize: 1000,
            },
            projectId: projectId,
        };
        getUserList(request);
    };

    const handleFilterValueChange = (filterId, filterKey, selectedFilter) => {
        // Changing the field invalidates the previously picked value
        if (filterKey === "selectedFilterKey") {
            updateFilter(filterId, {
                selectedFilterKey: selectedFilter,
                selectedFilterType: null,
            });
            return;
        }

        updateFilter(filterId, { [filterKey]: selectedFilter });
    };

    const getValueOptions = (filter) => apiCallMapping[filter.selectedFilterKey?.value] || [];

    useEffect(() => {
        if (isUserName && !isUserNameFetching && UserNameData) {
            const userNameList = UserNameData.dataSource.map((item) => ({
                value: item.userId,
                label: item.userName,
            })).sort((a, b) => a.label.localeCompare(b.label));
            setUserList(userNameList);
        }
    }, [isUserName, isUserNameFetching, UserNameData]);

    useEffect(() => {
        if (isgetTeamPlanningListByUserIdsuccess && !isgetTeamPlanningListByUserIdfetching && isgetTeamPlanningListByUserIdData) {
            const planningListData = isgetTeamPlanningListByUserIdData.map(
                (item) => ({
                    value: item.teamPlanningId,
                    label: `${formatDate(item.startDate, "DD MMM, YYYY")} - ${formatDate(
                        item.endDate,
                        "DD MMM, YYYY"
                    )}`,
                })
            );
            setPlanningList(planningListData);
        }
    }, [isgetTeamPlanningListByUserIdsuccess, isgetTeamPlanningListByUserIdfetching, isgetTeamPlanningListByUserIdData,]);

    useEffect(() => {
        if (!isTaskStatusfetching && isTaskStatussuccess && isTaskStatusData) {
            if (isTaskStatusData) {
                let status = isTaskStatusData.dataSource.map((item) => ({
                    value: item.projectStatusConfigurationId,
                    label: item.statusName,
                    isActive: item.isActive,
                    isComplete: item.isComplete,
                    className: item.isComplete === true ? "green-text" : "blue-text",
                })).sort((a, b) => a.label.localeCompare(b.label));
                status = status.filter((f) => f.isActive);
                setTaskStatus(status);
            }
        }
    }, [isTaskStatusfetching, isTaskStatussuccess, isTaskStatusData]);

    useEffect(() => {
        if (!isPriorityFetching && isPrioritySuccess && isPriorityData) {
            const priorityList = isPriorityData.map((item) => ({
                value: item.priorityId,
                label: item.priorityName,
            })).sort((a, b) => a.label.localeCompare(b.label));
            setTaskPriority(priorityList);
        }
    }, [isPriorityFetching, isPrioritySuccess, isPriorityData]);

    useEffect(() => {
        if (!isTaskTypeFetching && isTaskTypeFetched && TaskTypeData) {
            const badgeOptions = TaskTypeData.map((item) => ({
                value: item.taskTypeId,
                label: item.taskTypeName
            })).sort((a, b) => a.label.localeCompare(b.label));
            setTaskTypes(badgeOptions);
        }
    }, [isTaskTypeFetching, isTaskTypeFetched, TaskTypeData]);

    useEffect(() => {
        if (isListSuccess && ProjectList) {
            const projectOptions = ProjectList.map((item) => ({
                value: item.projectId,
                label: item.projectName
            })).sort((a, b) => a.label.localeCompare(b.label));
            setProjects(projectOptions);
        }
    }, [isListSuccess, ProjectList]);

    // Clear filters when ProjectId change
    useEffect(() => {
        if (projectId) {
            clearFilters();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const apiCallMapping = {
        taskTypeName: taskTypes,
        priority: taskPriority,
        status: taskStatus,
        assignee: userList,
        planningName: planningList,
        projectName: projects
    };

    const appliedFilterCount = (filterRequest || []).length;

    return (
        <DropdownWrapper
            toggleElement={
                <div className={`filter-btn ${appliedFilterCount > 0 ? "active" : ""}`}>
                    <button type="button">
                        <Iconify icon="gridicons:filter" width={16} />
                        <span>
                            {appliedFilterCount > 0
                                ? `${appliedFilterCount} Filter${appliedFilterCount !== 1 ? "s" : ""}`
                                : "Filter"}
                        </span>
                    </button>
                </div>
            }
        >
            <div className="filter-dropdown">
                <div className="filter-dropdown-header">
                    <div className="filter-dropdown-title">Filters</div>
                    <button className="clear-btn" onClick={clearFilters}>
                        Clear All
                    </button>
                </div>
                <div className="filter-dropdown-content">
                    {filters.map((filter, index) => (
                        <div className="filter-dropdown-item" key={filter.id}>
                            {/* Display 'Where' only if there are more than one filter */}
                            {filters.length > 1 && index === 0 && (
                                <div className="filter-connector">Where</div>
                            )}
                            {/* Display 'OR' for subsequent filters */}
                            {index > 0 && <div className="filter-connector">AND</div>}

                            <div className="filter-type-dropdown">
                                <DropdownSelect
                                    value={filter.selectedFilterKey}
                                    optionsValue={filterDropdownKeys(taskFilterPageId)}
                                    // handleDropdownChange={(selectedOption) => updateFilter(filter.id, "selectedFilterKey", selectedOption)}
                                    handleDropdownChange={(selectedOption) => handleFilterValueChange(filter.id, "selectedFilterKey", selectedOption)}
                                    placeholder="Select filter" />
                            </div>
                            <div className="filter-is-dropdown">
                                <DropdownSelect
                                    value={filter.selectedFilterCompareKey}
                                    optionsValue={filterCompare}
                                    // handleDropdownChange={(selectedOption) => updateFilter(filter.id, "selectedFilterCompareKey", selectedOption)}
                                    handleDropdownChange={(selectedOption) => handleFilterValueChange(filter.id, "selectedFilterCompareKey", selectedOption)}
                                    placeholder="filter condition" />
                            </div>
                            <div className="filter-type-status-dropdown">
                                <DropdownSelect
                                    value={filter.selectedFilterType}
                                    optionsValue={getValueOptions(filter)}
                                    // handleDropdownChange={(selectedOption) => updateFilter(filter.id, "selectedFilterType", selectedOption)}
                                    handleDropdownChange={(selectedOption) => handleFilterValueChange(filter.id, "selectedFilterType", selectedOption)}
                                    placeholder="filter value" />
                            </div>
                            {filters.length > 1 && (
                                <div className="delete-icon">
                                    <Iconify
                                        icon="material-symbols:delete-outline"
                                        onClick={() => deleteFilter(filter.id)}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="filter-dropdown-footer d-flex justify-content-between">
                    <button className="filter-footer-button" onClick={addFilter}>
                        <Iconify icon="si:add-fill" />
                        Add Filter
                    </button>
                    <button className="filter-footer-button" onClick={applyFilter}>
                        <Iconify icon="si:add-fill" />
                        Apply
                    </button>
                </div>
            </div>
        </DropdownWrapper>
    );
};

export default FilterDropdownTest;
